// Subscription state management - Server-side enforcement
// User ID from Google Auth or localStorage fallback

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEYS = {
  USER_ID: 'decisora_user_id',
  USER_DATA: 'decisora_user',
  CACHED_STATUS: 'decisora_cached_status',
  CACHED_PRO_STATUS: 'decisora_cached_pro_status',
  CACHE_TIME: 'decisora_cache_time'
};

const CACHE_DURATION = 30000; // 30 seconds cache

// Get user ID (from auth or localStorage)
export const getUserId = () => {
  // First check for authenticated user
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user.user_id) return user.user_id;
    } catch {}
  }
  
  // Fallback to anonymous user ID
  let userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  if (!userId) {
    userId = crypto.randomUUID ? crypto.randomUUID() : 
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  }
  return userId;
};

// Get authenticated user data
export const getAuthUser = () => {
  const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {}
  }
  return null;
};

// Check if user is logged in
export const isLoggedIn = () => {
  return !!getAuthUser();
};

// Check if user has active premium
export const isPremium = () => {
  const user = getAuthUser();
  if (!user) return false;
  if (!user.paid_until) return false;
  return new Date(user.paid_until) > new Date();
};

// Login with Google
export const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Logout
export const logout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
  } catch {}
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  localStorage.removeItem(STORAGE_KEYS.CACHED_STATUS);
  localStorage.removeItem(STORAGE_KEYS.CACHED_PRO_STATUS);
  localStorage.removeItem(STORAGE_KEYS.CACHE_TIME);
  // Keep USER_ID for anonymous usage
  window.location.reload();
};

// Verify session with server
export const verifySession = async () => {
  try {
    const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
    const user = response.data;
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEYS.USER_ID, user.user_id);
    return user;
  } catch {
    // Session invalid - clear stored user
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    return null;
  }
};

// Get cached status if still valid
const getCachedStatus = () => {
  try {
    const cacheTime = localStorage.getItem(STORAGE_KEYS.CACHE_TIME);
    if (cacheTime && Date.now() - parseInt(cacheTime) < CACHE_DURATION) {
      const cached = localStorage.getItem(STORAGE_KEYS.CACHED_STATUS);
      if (cached) return JSON.parse(cached);
    }
  } catch {
    // Ignore cache errors
  }
  return null;
};

// Get cached pro status
const getCachedProStatus = () => {
  try {
    const cacheTime = localStorage.getItem(STORAGE_KEYS.CACHE_TIME);
    if (cacheTime && Date.now() - parseInt(cacheTime) < CACHE_DURATION) {
      const cached = localStorage.getItem(STORAGE_KEYS.CACHED_PRO_STATUS);
      if (cached) return JSON.parse(cached);
    }
  } catch {
    // Ignore cache errors
  }
  return null;
};

// Save status to cache
const cacheStatus = (status, proStatus) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CACHED_STATUS, JSON.stringify(status));
    if (proStatus) {
      localStorage.setItem(STORAGE_KEYS.CACHED_PRO_STATUS, JSON.stringify(proStatus));
    }
    localStorage.setItem(STORAGE_KEYS.CACHE_TIME, Date.now().toString());
  } catch {
    // Ignore cache errors
  }
};

// Clear cache (call after decisions or payments)
export const clearStatusCache = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.CACHED_STATUS);
    localStorage.removeItem(STORAGE_KEYS.CACHED_PRO_STATUS);
    localStorage.removeItem(STORAGE_KEYS.CACHE_TIME);
  } catch {
    // Ignore
  }
};

// Update user premium status after payment
export const updateUserPremiumStatus = (packageId) => {
  try {
    // Update decisora_user with premium status
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      userData.plan = packageId;
      userData.is_premium = true;
      userData.paid_until = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    }
    // Clear status cache to force refresh
    clearStatusCache();
  } catch {
    // Ignore errors
  }
};

// Fetch user status from server (regular decisions)
export const fetchUserStatus = async (forceRefresh = false) => {
  // Check cache first
  if (!forceRefresh) {
    const cached = getCachedStatus();
    if (cached) return cached;
  }
  
  const userId = getUserId();
  
  try {
    const response = await axios.get(`${API}/user/status/${userId}`);
    const status = response.data;
    cacheStatus(status, null);
    return status;
  } catch (error) {
    console.error('Failed to fetch user status:', error);
    // Return fallback for free user
    return {
      user_id: userId,
      plan: 'free',
      can_decide: true,
      remaining: 3,
      limit_type: 'daily',
      needs_payment: false,
      trial_used: false,
      message: null
    };
  }
};

// Fetch pro feature status (Challenge My Choice, Deep Reasoning)
export const fetchProStatus = async (forceRefresh = false) => {
  // Check cache first
  if (!forceRefresh) {
    const cached = getCachedProStatus();
    if (cached) return cached;
  }
  
  const userId = getUserId();
  
  try {
    const response = await axios.get(`${API}/user/pro-status/${userId}`);
    const proStatus = response.data;
    const cachedStatus = getCachedStatus();
    cacheStatus(cachedStatus, proStatus);
    return proStatus;
  } catch (error) {
    console.error('Failed to fetch pro status:', error);
    // Return fallback
    return {
      user_id: userId,
      plan: 'free',
      can_use: true,
      trial_used: false,
      trial_available: true,
      needs_payment: false,
      remaining: 1,
      button_text: 'Try once free',
      message: null
    };
  }
};

// Use a pro feature (API call)
export const callProFeature = async (sessionId, feature) => {
  const userId = getUserId();
  
  try {
    const response = await axios.post(`${API}/pro/use-feature`, {
      session_id: sessionId,
      user_id: userId,
      feature: feature
    });
    
    // Clear cache after using feature
    clearStatusCache();
    
    return response.data;
  } catch (error) {
    console.error('Failed to use pro feature:', error);
    throw error;
  }
};

// Synchronous getters using cached data (for UI)
export const getSubscriptionTier = () => {
  const cached = getCachedStatus();
  return cached?.plan || 'free';
};

export const isPaidUser = () => {
  const plan = getSubscriptionTier();
  return plan === 'decision_partner' || plan === 'enterprise';
};

export const getRemainingDecisions = () => {
  const cached = getCachedStatus();
  return cached?.remaining ?? 3;
};

export const canMakeDecision = () => {
  const cached = getCachedStatus();
  return cached?.can_decide ?? true;
};

export const needsPayment = () => {
  const cached = getCachedStatus();
  return cached?.needs_payment ?? false;
};

export const getTrialUsed = () => {
  const cached = getCachedProStatus();
  return cached?.trial_used ?? false;
};

export const getProButtonText = () => {
  const cached = getCachedProStatus();
  return cached?.button_text || 'Try once free';
};

export const getLimitMessage = () => {
  const cached = getCachedStatus();
  return cached?.message || null;
};

// Initialize user on app load
export const initializeUser = async () => {
  const userId = getUserId();
  try {
    const response = await axios.post(`${API}/user/register?user_id=${userId}`);
    // Fetch both statuses
    const status = await fetchUserStatus(true);
    const proStatus = await fetchProStatus(true);
    return { ...status, ...proStatus };
  } catch {
    // User might already exist, fetch status anyway
    const status = await fetchUserStatus(true);
    const proStatus = await fetchProStatus(true);
    return { ...status, ...proStatus };
  }
};
